import { usePlayers } from '@/hooks/HostSessionControlHooks';
import { useEndSession } from '@/hooks/HostSessionControlHooks';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import AnimatedSummary from '@/components/Animated/Backgrounds/AnimatedSummaryBackground';
import PlayerAnsweringItem from '@/components/Host/PlayerAnsweringItem';
import OldNavMenu from '../Navigation/OldNavMenu';

const ThanksScreen = ({ onTransitionToCards }: { onTransitionToCards: () => void }) => {
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    let timer1: NodeJS.Timeout;
    let timer2: NodeJS.Timeout;

    if (animationStage === 0) {
      timer1 = setTimeout(() => {
        setAnimationStage(1);
      }, 2000);
    }

    if (animationStage === 1) {
      timer2 = setTimeout(() => {
        onTransitionToCards();
      }, 3000);
    }

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [animationStage, onTransitionToCards]);

  return (
    <motion.div
      className="flex flex-col items-center justify-center fixed inset-0"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{
        opacity: 1,
        scale: 1,
        transition: { duration: 0.6, type: 'spring', bounce: 0.4 }
      }}
      exit={{ opacity: 0, scale: 1.2, transition: { duration: 0.4 } }}
    >
      <AnimatePresence mode="wait">
        {animationStage === 0 && (
          <motion.div
            key="thanks"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { type: 'spring', stiffness: 100 } }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
          >
            <motion.h1
              className="text-5xl font-extrabold text-[#20145F] text-center"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } }}
            >
              One Last Thing...
            </motion.h1>
          </motion.div>
        )}

        {animationStage === 1 && (
          <motion.div
            key="transition"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } }}
            className="text-4xl text-[#20145F] text-center"
          >
            We have a few questions for you!
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const WaitingText = () => {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      className="relative flex flex-col items-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.h1
        className="text-4xl font-bold text-[#20145F] text-center mb-8"
        animate={{ scale: [1, 1.02, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        Waiting for everyone to answer
        <span className="inline-block w-64 text-left">{'.'.repeat(dotCount)}</span>
      </motion.h1>
      <motion.p
        className="text-lg text-[#20145F]/70"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        Please be patient while everyone finishes up!
      </motion.p>
    </motion.div>
  );
};

export default function HostGameWaitingAnswer() {
  const [showCards, setShowCards] = useState(false);
  const [allAnswered, setAllAnswered] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const players = usePlayers();

  const uniquePlayers = (() => {
    const byId = new Map<string, (typeof players)[number]>();
    players.forEach((p) => byId.set(p.id, p));
    return Array.from(byId.values());
  })();

  const endSession = useEndSession();

  const isAnswered = (player: (typeof uniquePlayers)[number]) =>
    player.feelingAfter !== null &&
    player.feelingAfter !== undefined &&
    player.questionAnswer !== null &&
    player.questionAnswer !== undefined;

  useEffect(() => {
    if (uniquePlayers.length > 0 && uniquePlayers.every((player) => isAnswered(player))) {
      setAllAnswered(true);
      const timer = setTimeout(() => {
        endSession();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [uniquePlayers, endSession]);

  useEffect(() => {
    if (allAnswered) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            endSession();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [allAnswered, endSession]);

  return (
    <>
      <AnimatedSummary />
      <AnimatePresence>
        {!showCards ? (
          <ThanksScreen key="thanks" onTransitionToCards={() => setShowCards(true)} />
        ) : (
          <div className="fixed inset-0 w-full h-full bg-center bg-no-repeat bg-cover">
            <OldNavMenu includeRole={false} includeProfile={true} includeLogo={false} />
            <div className="relative max-w-2xl mx-auto h-full flex flex-col items-center justify-center">
              <div className="absolute inset-x-0 top-0">
                <WaitingText />
              </div>

              <div className="absolute inset-x-0 top-96 bottom-192">
                <div
                  className="h-full px-16 overflow-y-auto"
                  style={{
                    scrollbarColor: '#f58c29 #f1f1f1',
                    scrollbarWidth: 'thin',
                    scrollPadding: '0 4px'
                  }}
                >
                  <motion.ul
                    className="flex flex-wrap gap-16 w-full max-w-5xl mx-auto py-16"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    {uniquePlayers.map((player) => (
                      <li key={player.id} className="w-[calc(50%-0.5rem)]">
                        <PlayerAnsweringItem
                          text={player.name}
                          subtext={isAnswered(player) ? 'Answered!' : 'Still thinking...'}
                          selected={isAnswered(player)}
                        />
                      </li>
                    ))}
                  </motion.ul>
                </div>
              </div>

              <div className="absolute bottom-96 left-0 right-0 flex justify-center">
                <motion.button
                  onClick={endSession}
                  className="btn btn-secondary btn-lg transition-all"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <AnimatePresence mode="wait">
                    {allAnswered ? (
                      <motion.div
                        key="countdown"
                        className="flex items-center justify-center space-x-8 w-full"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                      >
                        <span className="font-thin">Continuing in</span>
                        <motion.span
                          key={countdown}
                          initial={{ scale: 1.5, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.5, opacity: 0 }}
                        >
                          {countdown}
                        </motion.span>
                      </motion.div>
                    ) : (
                      <motion.span
                        key="continue"
                        className="block w-full text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                      >
                        Continue
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
